/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-rest-params */
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TableApiResponseInterface } from '../../+external-interfaces';
import { ContextInterface, EduContentInterface, EduContentMetadataInterface } from '../../+models';
import { EduContentDraftInterface, EduContentMergeResponseInterface } from '../../edu-content';
import { ModelApiBase, PolpoApi } from '../shared';

// this is a copy from libs/shared/src/lib/services/content/open-static-content.interface.ts
interface OpenStaticContentOptions {
  stream?: boolean;
  showPreview?: boolean;
  useIFrame?: boolean;
  context?: ContextInterface;
  disableMathjax?: boolean;
  eduContentMetadataId?: number;
}

@Injectable({ providedIn: 'root' })
export class EduContentApi extends ModelApiBase {
  constructor(api: PolpoApi) {
    super(api, 'EduContents');
  }

  requestURLRemote(
    contentId: number,
    stream?,
    taskId?: number,
    unlockedContentId?: number,
    unlockedFreePracticeId?: number,
    context?
  ): Observable<any> {
    const path = `${contentId}/requestURL`;

    // add fallback to undefined, so `null` values are not passed as params (which cause validation errors)
    const params = this._getHttpParams({
      stream: stream || false,
      taskId: taskId || undefined,
      unlockedContentId: unlockedContentId || undefined,
      unlockedFreePracticeId: unlockedFreePracticeId || undefined,
      context,
    });

    return this.get(path, params);
  }

  openStaticContentUrl(contentId: number, openStaticContentOptions: OpenStaticContentOptions): Observable<any> {
    const { path, params } = this.generateOpenStaticContentUrl(contentId, openStaticContentOptions);

    return this.get(path, params);
  }

  getStaticContentPath(contentId: number, openStaticContentOptions: OpenStaticContentOptions): string {
    const { path, params } = this.generateOpenStaticContentUrl(contentId, openStaticContentOptions);

    return `/api/EduContents/${path}?${params.toString()}`;
  }

  getLatestEduContents(): Observable<EduContentDraftInterface[]> {
    const path = 'get-latest-edu-contents';
    return this.get<EduContentDraftInterface[]>(path, undefined);
  }

  getMyEduContents(): Observable<EduContentDraftInterface[]> {
    const path = 'get-my-edu-contents';
    return this.get<EduContentDraftInterface[]>(path, undefined);
  }

  getGeneralEduContentForBookId(bookId: number): Observable<EduContentInterface[]> {
    const path = 'getGeneralEduContentForBookId';
    const params = this._getHttpParams({ bookId });
    return this.get<EduContentInterface[]>(path, params);
  }

  getEduContents<T = EduContentMetadataInterface>(
    filters?: any,
    pagination?: any,
    columns?: any[],
    sorting?: any,
    searchTerm?: string
  ): Observable<TableApiResponseInterface<T>> {
    const path = 'v2/filtered';
    const params = this._getHttpParams({ searchTerm, filters, pagination, columns, sorting });
    return this.get<TableApiResponseInterface<T>>(path, params);
  }

  search(state: any): Observable<any> {
    const path = 'search';
    return this.post<any>(path, undefined, { state });
  }

  // NOTE: The backend autocomplete endpoint is currently a stub and does not return suggestions.
  // It only returns user permissions. Real autocomplete functionality is not implemented yet.
  autocomplete(state: any): Observable<string[]> {
    const path = 'autocomplete';
    return this.post<string[]>(path, undefined, { state });
  }

  updateEduContentDraft(eduContentId, changes): Observable<any> {
    const path = `${eduContentId}/draft`;
    return this.patch<number>(path, undefined, changes);
  }

  getEduContentDraft(eduContentId: number): Observable<EduContentDraftInterface> {
    const path = `get-edu-content-draft/${eduContentId}`;
    return this.get<EduContentDraftInterface>(path, undefined);
  }

  updateEduContentDraftRelation(eduContentId: number, body): Observable<any> {
    const path = `${eduContentId}/draft/relation`;
    return this.post<number>(path, undefined, body);
  }

  private generateOpenStaticContentUrl(
    contentId: number,
    { stream, showPreview, useIFrame, context, eduContentMetadataId }: OpenStaticContentOptions
  ): { path: string; params: HttpParams } {
    const isIFrameOrPreview = useIFrame || showPreview;
    const requestOrRedirect = isIFrameOrPreview ? 'redirectURL' : 'requestURL';

    const path =
      eduContentMetadataId && isIFrameOrPreview
        ? `${contentId}/${requestOrRedirect}/${eduContentMetadataId}`
        : `${contentId}/${requestOrRedirect}`;

    const params = this._getHttpParams({
      ...(stream && { stream }),
      ...(context && { context }),
      ...(eduContentMetadataId && { eduContentMetadataId }),
    });
    return { path, params };
  }

  canMergePdfs(
    eduContentIds: number[]
  ): Observable<{ errors: EduContentMergeResponseInterface[]; success: EduContentMergeResponseInterface[] }> {
    const path = `can-merge-pdfs`;

    const params = new HttpParams({
      fromObject: {
        eduContentIds: JSON.stringify(eduContentIds),
      },
    });

    return this.get<{ errors: EduContentMergeResponseInterface[]; success: EduContentMergeResponseInterface[] }>(
      path,
      params
    );
  }

  getEduContentByMetadataId(metadataId: number): Observable<EduContentInterface> {
    const path = `get-by-metadata/${metadataId}`;
    return this.get<EduContentInterface>(path, undefined);
  }
}
